import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Container, Paper, Typography } from '@mui/material';
import AuthLayout from '../../layout/auth.layout';
import Logo from '../../components/common/logo.component';
import Login from '../../components/auth/login.component';
import PAGE_TITLE_LIST from '../../config/page-title.config';
import { APP_THEME } from '../../theme/theme-colors';

const ImageBackground = styled('div')(({ theme }) => ({
  width: '100vw',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `
    radial-gradient(circle at top left, rgba(2, 79, 160, 0.16), transparent 32%),
    radial-gradient(circle at bottom right, rgba(2, 79, 160, 0.12), transparent 28%),
    linear-gradient(135deg, #eef4fb 0%, #f8fafc 45%, #edf3f9 100%)
  `,
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(3, 0),
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(10px)',
  },
  '&::before': {
    width: 320,
    height: 320,
    background: 'rgba(2, 79, 160, 0.08)',
    top: -120,
    left: -60,
  },
  '&::after': {
    width: 280,
    height: 280,
    background: 'rgba(13, 28, 45, 0.08)',
    bottom: -120,
    right: -80,
  },
}));

const StyledRoot = styled('div')(() => ({
  width: '100%',
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
}));

const StyledContent = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(6, 5),
  backgroundColor: 'rgba(255, 255, 255, 0.96)',
  backdropFilter: 'blur(18px)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 3),
  },
}));

const StyledShell = styled(Paper)(() => ({
  overflow: 'hidden',
  borderRadius: 28,
  border: '1px solid rgba(145, 158, 171, 0.18)',
  background: 'rgba(255, 255, 255, 0.72)',
  boxShadow: '0 32px 80px rgba(15, 23, 42, 0.14)',
  maxWidth: 560,
  margin: '0 auto',
}));

const LoginView = () => {
  const pageTitle = PAGE_TITLE_LIST.LOGIN;

  return (
    <AuthLayout pageTitle={pageTitle}>
      <ImageBackground>
        <StyledRoot>
          <Container maxWidth="sm">
            <StyledShell elevation={0}>
              <StyledContent square elevation={0}>
                <Box sx={{ width: '100%', maxWidth: 430, mx: 'auto' }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}
                  >
                    <Logo
                      sx={{
                        width: { xs: 200, md: 230 },
                        cursor: 'default',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="overline"
                    align="center"
                    sx={{
                      display: 'block',
                      color: APP_THEME,
                      letterSpacing: 1.6,
                      fontWeight: 700,
                    }}
                  >
                    Welcome back
                  </Typography>

                  <Typography
                    variant="h3"
                    align="center"
                    sx={{ mt: 1, mb: 1.5, fontSize: { xs: 30, md: 34 } }}
                  >
                    Sign in to continue
                  </Typography>

                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.8 }}
                  >
                    Use your admin credentials to access the CMS dashboard and
                    continue managing platform content.
                  </Typography>

                  <Login />
                </Box>
              </StyledContent>
            </StyledShell>
          </Container>
        </StyledRoot>
      </ImageBackground>
    </AuthLayout>
  );
};

export default LoginView;
